<template>
  <ul v-if="(validOptionGroups && validOptionGroups.length > 0) || props.groupMembers" class="py-2">
    <li v-if="props.groupMembers" class="pt-4">
      <OptionGroupTitle :title="t['w.module.options_section.variation']()" :option="selectedGroupMember" @click="toggleDrawer('groupMember', props.groupMembers.length)" />
      <VueFinalModal
        v-if="activeDrawer === 'groupMember'"
        v-model="isDrawerOpen"
        content-class="pointer-events-none fixed inset-0"
        :z-index-base="100000"
        esc-to-close
        :content-transition="{
          enterActiveClass: 'transition duration-200 ease-in-out transform',
          enterFromClass: 'translate-x-full',
          enterToClass: 'translate-x-0',
          leaveActiveClass: 'transition duration-200 ease-in-out transform',
          leaveToClass: 'translate-x-full',
          leaveFromClass: 'translate-x-0',
        }"
      >
        <div class="pointer-events-auto ml-auto flex size-full flex-col bg-background px-6 sm:px-8 md:w-[440px] md:px-10">
          <div class="flex items-start justify-between border-b border-border py-8">
            <h3>{{ t['w.module.options_section.variation']() }}</h3>
            <button
              class="group ml-3 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-background-light active:bg-background-light"
              @click="toggleDrawer('groupMember', props.groupMembers.length)"
            >
              <span class="sr-only">{{ t['w.module.options_section.close']() }}</span>
              <Icon name="close" class="size-6 group-active:text-icon-active" />
            </button>
          </div>
          <div class="grow overflow-y-auto">
            <GroupMemberOptionSection :members="sortedGroupMembers" option-group-id="groupMember" @selected-option="gotoGroupMember" />
          </div>
        </div>
      </VueFinalModal>
    </li>
    <li v-for="optionGroup of validOptionGroups" :key="optionGroup.id" class="pt-4">
      <OptionGroupTitle
        :key="optionGroup.id"
        :title="optionGroup.name"
        :description="optionGroup.description"
        :option="selectedOptions[optionGroup.id]"
        :is-text-option="optionGroup.displayStyleId === 1 || optionGroup.displayStyleId === 7"
        :is-image-option="optionGroup.displayStyleId === 4 || optionGroup.displayStyleId === 5"
        :is-color-option="optionGroup.displayStyleId === 6"
        :is-single-item="optionGroup.options.length === 1"
        :is-error="props.highlightEmptyOptions && !(selectedOptions[optionGroup.id] || selectedGroupMember)"
        @click="toggleDrawer(optionGroup.id, optionGroup.options.length)"
      />
      <VueFinalModal
        v-if="activeDrawer === optionGroup.id"
        :key="optionGroup.id"
        v-model="isDrawerOpen"
        :class="`option-group-${optionGroup.id}`"
        content-class="pointer-events-none fixed inset-0"
        :z-index-base="100000"
        esc-to-close
        :content-transition="{
          enterActiveClass: 'transition duration-200 ease-in-out transform',
          enterFromClass: 'translate-x-full',
          enterToClass: 'translate-x-0',
          leaveActiveClass: 'transition duration-200 ease-in-out transform',
          leaveToClass: 'translate-x-full',
          leaveFromClass: 'translate-x-0',
        }"
      >
        <div class="pointer-events-auto ml-auto flex size-full flex-col bg-background px-6 sm:px-8 md:w-[440px] md:px-10">
          <div class="flex items-start justify-between border-b border-border py-8">
            <div class="flex items-center">
              <h3>{{ optionGroup.name }}</h3>
              <InfoTooltip v-if="optionGroup.description" class="ml-2">{{ optionGroup.description }}</InfoTooltip>
            </div>
            <button
              class="group ml-3 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full hover:bg-background-light active:bg-background-light"
              @click="toggleDrawer(optionGroup.id, optionGroup.options.length)"
            >
              <span class="sr-only">{{ t['w.module.options_section.close']() }}</span>
              <Icon name="close" class="size-6 group-active:text-icon-active" />
            </button>
          </div>
          <div class="grow overflow-y-auto">
            <TextOptionsSection
              v-if="optionGroup.displayStyleId === 1"
              :key="`option-section-${optionGroup.id}`"
              :options="optionGroup.options"
              :option-group-id="optionGroup.id"
              :option-group-title="optionGroup.name"
              :option-group-description="optionGroup.description"
              :is-option-available="isOptionAvailable"
              :selected-option-ids="selectedOptionIds"
              :option-group-count="validOptionGroups.length"
              @selected-option="onInput($event, optionGroup.id)"
            />
            <ImageOptionsSection
              v-else-if="optionGroup.displayStyleId === 4 || optionGroup.displayStyleId === 5"
              :model="selectedOptions[optionGroup.id]"
              :options="optionGroup.options"
              :option-group-id="optionGroup.id"
              :option-group-title="optionGroup.name"
              :option-group-description="optionGroup.description"
              :is-option-available="isOptionAvailable"
              :selected-option-ids="selectedOptionIds"
              :option-group-count="validOptionGroups.length"
              @selected-option="onInput($event, optionGroup.id)"
            />
            <ColorOptionsSection
              v-else-if="optionGroup.displayStyleId === 6"
              :model="selectedOptions[optionGroup.id]"
              :options="optionGroup.options"
              :option-group-id="optionGroup.id"
              :option-group-title="optionGroup.name"
              :option-group-description="optionGroup.description"
              :is-option-available="isOptionAvailable"
              :selected-option-ids="selectedOptionIds"
              :option-group-count="validOptionGroups.length"
              @selected-option="onInput($event, optionGroup.id)"
            />
          </div>
          <div class="flex items-center justify-center border-t border-border py-8 md:hidden">
            <Button class="btn-lg btn-rounded w-full" @click="toggleDrawer(optionGroup.id, optionGroup.options.length)">{{ t['w.module.options_bar.apply']() }}</Button>
          </div>
        </div>
      </VueFinalModal>
    </li>
  </ul>
</template>

<script setup lang="ts">
import TextOptionsSection from './selector/TextOptionsSection.vue'
import GroupMemberOptionSection from './selector/GroupMemberOptionSection.vue'
import ImageOptionsSection from './selector/ImageOptionsSection.vue'
import ColorOptionsSection from './selector/ColorOptionsSection.vue'
import OptionGroupTitle from './selector/OptionGroupTitle.vue'
import InfoTooltip from '~/components/info-tooltip/InfoTooltip.vue'
import { VueFinalModal } from 'vue-final-modal'
import * as t from '~/messages'
import { useBreakpoint } from '~/composables/breakpoint'

const props = defineProps({
  optionGroups: { type: Array, default: null },
  groupMembers: { type: Array, default: null },
  productId: { type: Number, default: null },
  highlightEmptyOptions: { type: Boolean, default: false },
  variations: { type: Array, default: () => [] },
})

const selectedGroupMember = ref(null)
const selectedOptions = ref({})
const localGroupMembers = ref(props.groupMembers)
const activeDrawer = ref<string | null>(null)
const isDrawerOpen = ref<boolean>(false)

const { isBreakpoint } = useBreakpoint()

const toggleDrawer = (drawerId, optionsGroupLength) => {
  if (optionsGroupLength !== undefined && optionsGroupLength > 1) {
    isDrawerOpen.value = !isDrawerOpen.value
    activeDrawer.value = drawerId
  }
}

const validOptionGroups = computed(() => {
  if (!props.optionGroups) {
    return null
  }

  return props.optionGroups.filter((g) => g.displayStyleId === 1 || g.displayStyleId === 3 || g.displayStyleId === 4 || g.displayStyleId === 5 || g.displayStyleId === 6)
})

const selectedOptionIds = computed(() => {
  return new Set(
    Object.values(selectedOptions.value)
      .filter((o) => o !== null)
      .map((o) => o.id)
  )
})

const availableOptionIdCombinations = computed(() => {
  const availableVariations = props.variations?.filter((v) => v.shippingTime !== 0)
  return new Set(availableVariations?.map((v) => v.optionIds))
})

const onInput = (option, optionGroupId) => {
  selectedOptions.value[optionGroupId] = option

  if (isBreakpoint('md').value) {
    isDrawerOpen.value = !isDrawerOpen.value
  }

  emitSelectedOptions()
}

const emit = defineEmits<{
  'selected-options': [selectedOptions: unknown]
}>()

const emitSelectedOptions = () => {
  emit('selected-options', selectedOptions.value)
}

const onGroupMembersChange = () => {
  if (props.groupMembers) {
    const existingSelection = props.groupMembers.find((m) => m.id === props.productId)
    if (existingSelection) {
      selectedGroupMember.value = existingSelection
    }
  }
}

const initializeOptions = (postClear) => {
  const optionShouldBeSelected = (o, onlyOption) => {
    return onlyOption || (postClear && o.hasVariationsForcedSelectedOption) || (!postClear && o.selectedByDefault)
  }

  const createSelectedOptions = () => {
    const sos = {}
    props.optionGroups.forEach((g) => {
      sos[g.id] = null
      const onlyOption = g.options.length === 1
      g.options.forEach((o) => {
        if (optionShouldBeSelected(o, onlyOption)) {
          sos[g.id] = o
        }
      })
    })
    return sos
  }

  selectedOptions.value = createSelectedOptions()
  emitSelectedOptions()
}

const gotoGroupMember = (option: any) => {
  navigateTo({
    path: option.productUri,
  })
}

const isOptionAvailable = (optionId, optionGroupId) => {
  if (optionGroupId === 0) {
    /* legacy option group (viimistlused) - all possible options allowed
        Not used in multiselect, but is used with small amount of options and mobile page */
    return true
  }

  const currentOptionGroup = props.optionGroups.find((og) => og.id === optionGroupId)
  if (!currentOptionGroup.doesCreateVariation) {
    /* options (that reach shop) are always available when they don't create variations */
    return true
  }

  const selectedOptionsPlusThisOption = new Set(selectedOptionIds.value)
  selectedOptionsPlusThisOption.add(optionId)

  /* Option is available if there are variations with option combinations which include
      selected options + this option */
  let doesAvailableOptionIdCombinationExist = false
  availableOptionIdCombinations.value.forEach((availableOptionIds) => {
    const availableOptionIdsSet = new Set(availableOptionIds)
    const availableSetContainsCurrentSelectionPlusThisOption = isSuperset(availableOptionIdsSet, selectedOptionsPlusThisOption)
    if (availableSetContainsCurrentSelectionPlusThisOption) {
      doesAvailableOptionIdCombinationExist = true
    }
  })

  /* If there is another option selected in same group, option should not be unavailable,
      otherwise all other options in same group get disabled after selection (we do not want that).
      Unavailability logic should only work for unselected groups.

      But the other option should only be available if there exists a possible combination with
      selected options from other groups */
  const selectedOptionInThisOptionsGroup = selectedOptions.value[optionGroupId]
  const isSomeOtherOptionFromSameOptionGroupSelected = selectedOptionInThisOptionsGroup !== null && selectedOptionInThisOptionsGroup.id !== optionId

  let isThereAPossibleCombinationWithThisOptionInsteadOfSelected = false
  if (isSomeOtherOptionFromSameOptionGroupSelected) {
    const selectedOptionsButWithThisOption = new Set(selectedOptionIds.value)
    selectedOptionsButWithThisOption.delete(selectedOptionInThisOptionsGroup.id)
    selectedOptionsButWithThisOption.add(optionId)

    availableOptionIdCombinations.value.forEach((availableOptionIds) => {
      const availableOptionIdsSet = new Set(availableOptionIds)
      const availableSetContainsCurrentSelectionPlusThisReplacedOption = isSuperset(availableOptionIdsSet, selectedOptionsButWithThisOption)
      if (availableSetContainsCurrentSelectionPlusThisReplacedOption) {
        isThereAPossibleCombinationWithThisOptionInsteadOfSelected = true
      }
    })
  }

  const selectedSetSize = selectedOptionIds.value.size > 0 ? true : false

  if (!selectedSetSize && !doesAvailableOptionIdCombinationExist && !isThereAPossibleCombinationWithThisOptionInsteadOfSelected) {
    return 0
  } else if (selectedSetSize && !doesAvailableOptionIdCombinationExist && !isThereAPossibleCombinationWithThisOptionInsteadOfSelected) {
    return null
  } else if (doesAvailableOptionIdCombinationExist || isThereAPossibleCombinationWithThisOptionInsteadOfSelected) {
    return 1
  }
}

const isSuperset = (set, subset) => {
  for (const elem of subset) {
    if (!set.has(elem)) {
      return false
    }
  }

  return true
}

// TODO: Save these in store across route changes, because customer usually compares e.g. 5 products and goes back and forth
watch(
  () => props.optionGroups,
  () => {
    selectedOptions.value = {}
  },
  {
    immediate: true,
  }
)

watch(
  () => props.groupMembers,
  (val) => {
    localGroupMembers.value = val
    onGroupMembersChange()
  },
  {
    immediate: true,
  }
)

if (props.optionGroups) {
  initializeOptions()
}

const sortedGroupMembers = ref(props.groupMembers)

if (sortedGroupMembers.value) {
  sortedGroupMembers.value.sort((a, b) => a.price - b.price)
}
</script>
